import { useCallback } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { captureEvent } from '../../lib/analytics';
import { insertTosAcceptance } from '@dramadetector/api-client';
import {
  TOS_STORAGE_KEY,
  CURRENT_TOS_VERSION,
  normalizeVersion,
} from './types-constants';
import { SafetyModalsAction } from './reducer';

export function useTosModal(
  dispatch: React.Dispatch<SafetyModalsAction>,
  tosVersion?: string,
  user?: { id: string } | null
) {
  const acceptTos = useCallback(
    async (version: string) => {
      const timestamp = new Date().toISOString();

      try {
        await AsyncStorage.setItem(
          TOS_STORAGE_KEY,
          JSON.stringify({
            accepted: true,
            timestamp,
            version,
          })
        );

        if (user?.id) {
          await insertTosAcceptance(version, 'modal');
        }

        dispatch({ type: 'ACCEPT_TOS', payload: { timestamp, version } });

        captureEvent('Safety_TosAccepted', {
          version,
          method: 'modal',
          is_update: tosVersion !== version && tosVersion !== undefined,
        });
      } catch (error) {
        console.error('[SafetyModals] Failed to persist ToS acceptance:', error);
        dispatch({ type: 'ACCEPT_TOS', payload: { timestamp, version } });
      }
    },
    [user?.id, tosVersion, dispatch]
  );

  const declineTos = useCallback(
    (version: string) => {
      dispatch({ type: 'DECLINE_TOS' });

      captureEvent('Safety_TosDeclined', {
        version,
        method: 'modal',
      });
    },
    [dispatch]
  );

  const checkTosAcceptance = useCallback(async (): Promise<boolean> => {
    try {
      const stored = await AsyncStorage.getItem(TOS_STORAGE_KEY);
      if (stored) {
        const { accepted, version } = JSON.parse(stored);
        const isCurrentVersion = normalizeVersion(version) === normalizeVersion(CURRENT_TOS_VERSION);
        return accepted && isCurrentVersion;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  return {
    acceptTos,
    declineTos,
    checkTosAcceptance,
  };
}
